import { useState } from 'react';
import { Calendar, Clock, Phone, Mail, MapPin, User, MessageSquare, ChevronRight, Home, CheckCircle, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Appointment = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        message: '',
      });
      setSubmitted(false);
    }, 5000);
  };

  return (
    <div className="pt-20 bg-[#fbfdff] min-h-screen">
      
      {/* --- 1. PREMIUM BREADCRUMB HEADER --- */}
      <section className="bg-gradient-to-br from-blue-900 to-teal-800 py-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-20 -mt-20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-teal-400/10 rounded-full -ml-10 -mb-10 blur-2xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="flex items-center gap-2 text-teal-200/80 text-sm font-bold uppercase tracking-widest mb-4">
            <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" /> Home
            </Link>
            <ChevronRight className="w-4 h-4 opacity-50" />
            <span className="text-white">Book Appointment</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">Schedule Consultation</h1>
          <div className="h-1.5 w-24 bg-teal-400 mt-4 rounded-full" />
          <p className="text-blue-100/70 mt-6 max-w-2xl font-medium text-lg">
            Book your slot with Dr. Sachitanand Chaurasia for expert medical care and personalized treatment.
          </p>
        </div>
      </section>

      {/* --- 2. MAIN APPOINTMENT SECTION --- */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            
            {/* Form Container */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 md:p-12 rounded-[3rem] shadow-2xl border border-slate-100">
                <div className="flex items-center gap-4 mb-10">
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-blue-700" />
                    </div>
                    <h2 className="text-3xl font-black text-slate-900 italic">Request a Slot</h2>
                </div>
                
                <AnimatePresence mode="wait">
                  {submitted ? (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-teal-50 border-2 border-teal-100 rounded-[2rem] p-10 text-center"
                    >
                      <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-teal-200">
                        <CheckCircle className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-2xl font-black text-teal-900 mb-2">Request Submitted!</h3>
                      <p className="text-teal-700 font-bold leading-relaxed">
                        Thank you. Our reception desk will call you within 24 hours to confirm your final appointment time.
                      </p>
                    </motion.div>
                  ) : (
                    <motion.form 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onSubmit={handleSubmit} 
                      className="space-y-6"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Full Name</label>
                          <div className="relative">
                            <User className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text" name="name" value={formData.name} onChange={handleChange} required
                                className="w-full pl-14 pr-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-bold text-slate-700"
                                placeholder="Patient Name"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Phone Number</label>
                          <div className="relative">
                            <Phone className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="tel" name="phone" value={formData.phone} onChange={handleChange} required
                                className="w-full pl-14 pr-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-bold text-slate-700"
                                placeholder="+91 00000 00000"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                        <div className="relative">
                            <Mail className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="email" name="email" value={formData.email} onChange={handleChange} required
                                className="w-full pl-14 pr-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-bold text-slate-700"
                                placeholder="patient@example.com"
                            />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Preferred Date</label>
                          <input
                            type="date" name="date" value={formData.date} onChange={handleChange} required
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-bold text-slate-700"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Preferred Time Slot</label>
                          <div className="relative">
                            <Clock className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                            <select
                                name="time" value={formData.time} onChange={handleChange} required
                                className="w-full pl-14 pr-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-bold text-slate-700 appearance-none"
                            >
                                <option value="">Select slot</option>
                                <option value="10:00 AM">10:00 AM</option>
                                <option value="12:00 PM">12:00 PM</option>
                                <option value="05:00 PM">05:00 PM</option>
                                <option value="07:00 PM">07:00 PM</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Symptoms / Reason</label>
                        <textarea
                          name="message" value={formData.message} onChange={handleChange} rows={4}
                          className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-bold text-slate-700"
                          placeholder="Briefly describe your medical concern..."
                        />
                      </div>

                      <button type="submit" className="w-full bg-slate-900 text-white px-10 py-5 rounded-2xl font-black text-lg hover:bg-blue-600 transition-all shadow-xl flex items-center justify-center gap-3 group">
                        Confirm Appointment Request 
                        <Calendar className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Sidebar Info */}
            <div className="lg:col-span-5 space-y-8">
              
              {/* OPD Timings Card */}
              <div className="bg-slate-900 p-10 rounded-[3rem] text-white relative overflow-hidden group">
                 <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl group-hover:bg-blue-500/20 transition-all" />
                 <h3 className="text-2xl font-black italic mb-8 flex items-center gap-3">
                    <Clock className="text-teal-400" /> OPD Hours
                 </h3>
                 <div className="space-y-6">
                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                        <span className="text-slate-400 font-bold">Mon - Sat</span>
                        <div className="text-right">
                            <p className="font-black">10:00 AM - 02:00 PM</p>
                            <p className="font-black text-teal-400">05:00 PM - 08:00 PM</p>
                        </div>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-slate-400 font-bold">Sunday</span>
                        <span className="bg-white/10 px-4 py-1 rounded-full font-black text-xs uppercase tracking-tighter">On Call Only</span>
                    </div>
                 </div>
              </div>

              {/* Emergency Card */}
              <div className="bg-red-50 p-8 rounded-[3rem] border-2 border-red-100 shadow-xl shadow-red-100/50">
                <div className="flex items-center gap-4 mb-4 text-red-600">
                    <div className="w-12 h-12 bg-red-600 rounded-2xl flex items-center justify-center">
                        <Phone className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-black uppercase tracking-widest text-xs">Emergency Helpline</h4>
                </div>
                <p className="text-3xl font-black text-red-900 mb-2">+91 1482 245679</p>
                <p className="text-red-700/70 font-bold text-sm leading-relaxed">Available 24/7 for critical medical emergencies.</p>
              </div>

              {/* Quick Checklist Card */}
              <div className="bg-blue-50 p-10 rounded-[3rem] border border-blue-100">
                <h3 className="font-black text-slate-900 mb-6 flex items-center gap-3">
                    <Info className="text-blue-600 w-5 h-5" /> Visit Checklist
                </h3>
                <ul className="space-y-4">
                    {[
                        "Carry previous medical reports",
                        "Bring current medication list",
                        "Valid Photo ID required",
                        "Arrive 15 mins prior to slot"
                    ].map((item, idx) => (
                        <li key={idx} className="flex items-center gap-3 font-bold text-slate-600 text-sm">
                            <div className="w-2 h-2 bg-blue-400 rounded-full" />
                            {item}
                        </li>
                    ))}
                </ul>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* --- 3. PROCESS STEPS --- */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-slate-900 mb-4 italic">How it Works</h2>
            <div className="h-1.5 w-20 bg-blue-600 mx-auto rounded-full" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
            {/* Step 1 */}
            <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-slate-900 text-white rounded-[2rem] flex items-center justify-center mx-auto text-3xl font-black shadow-xl rotate-3">1</div>
                <h3 className="text-xl font-black text-slate-900">Request Slot</h3>
                <p className="text-slate-500 font-bold">Fill the form with your details and preferred date/time.</p>
            </div>
            {/* Step 2 */}
            <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-blue-600 text-white rounded-[2rem] flex items-center justify-center mx-auto text-3xl font-black shadow-xl -rotate-3">2</div>
                <h3 className="text-xl font-black text-slate-900">Verification</h3>
                <p className="text-slate-500 font-bold">Our team will call you to confirm the availability.</p>
            </div>
            {/* Step 3 */}
            <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-teal-500 text-white rounded-[2rem] flex items-center justify-center mx-auto text-3xl font-black shadow-xl rotate-6">3</div>
                <h3 className="text-xl font-black text-slate-900">Get Treated</h3>
                <p className="text-slate-500 font-bold">Visit the hospital at confirmed time with your records.</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Appointment;